import { getApiErrorMessage } from '../../utils/apiError'
import { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import AccessTimeFilledRoundedIcon from '@mui/icons-material/AccessTimeFilledRounded'
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import PauseRoundedIcon from '@mui/icons-material/PauseRounded'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import StopRoundedIcon from '@mui/icons-material/StopRounded'

import { createDailyLog } from '../../features/daily-log'
import useModal from '../../hooks/useModal'
import {
  ACTIVITY_TYPES,
  createDailyLogPayload,
  formatTimerSeconds,
  TIMER_STATUS,
  useActivityTimerClock,
} from '../../features/timer'
import { getServiceDate } from '../../utils/serviceDate'
import { ROUTES } from '../../constants/routes'
import { Button, SelectInput, TextInput } from '../../components/ui'

const STATUS_LABEL = {
  [TIMER_STATUS.PREPARING]: '시작 준비',
  [TIMER_STATUS.RUNNING]: '측정 중',
  [TIMER_STATUS.PAUSED]: '일시정지',
  [TIMER_STATUS.UNSAVED]: '저장 필요',
  [TIMER_STATUS.SAVING]: '저장 중',
  [TIMER_STATUS.SAVE_UNKNOWN]: '저장 결과 확인 필요',
}

export default function GlobalTimerBar() {
  const timer = useActivityTimerClock()
  const navigate = useNavigate()
  const { openModal } = useModal()
  const [expanded, setExpanded] = useState(false)
  const [validationMessage, setValidationMessage] = useState('')
  const [savedNotice, setSavedNotice] = useState(false)

  const activityLabel = useMemo(
    () => ACTIVITY_TYPES.find(({ value }) => value === timer.dailyLogType)?.label || '활동',
    [timer.dailyLogType],
  )

  useEffect(() => {
    if ([TIMER_STATUS.PREPARING, TIMER_STATUS.UNSAVED, TIMER_STATUS.SAVING, TIMER_STATUS.SAVE_UNKNOWN].includes(timer.status)) {
      setExpanded(true)
    }
  }, [timer.status])

  useEffect(() => {
    if (!savedNotice) return undefined
    const timeoutId = window.setTimeout(() => setSavedNotice(false), 3000)
    return () => window.clearTimeout(timeoutId)
  }, [savedNotice])

  const validateContents = () => {
    if (timer.contents.trim()) {
      setValidationMessage('')
      return true
    }
    setValidationMessage('기록할 활동 내용을 입력해주세요.')
    return false
  }

  const handleStart = () => {
    if (!validateContents()) return
    timer.start()
    setExpanded(false)
  }

  const handleSave = async () => {
    if (!validateContents()) return
    if (!timer.startedAt || !timer.endedAt || timer.activityTimeSec <= 0) {
      setValidationMessage('측정 시간이 너무 짧아요. 1초 이상 측정해 주세요.')
      return
    }

    timer.markSaving()
    try {
      await createDailyLog(createDailyLogPayload(timer))
      const logOn = getServiceDate(timer.startedAt)
      timer.markSaved(logOn)
      setExpanded(false)
      setSavedNotice(true)
    } catch (error) {
      timer.markSaveFailed()
      openModal({
        type: 'alert',
        props: { isError: true, contents: getApiErrorMessage(error) },
      })
    }
  }

  const handleDiscard = () => {
    openModal({
      type: 'confirm',
      props: {
        contents: '측정한 기록을 저장하지 않고 삭제할까요?',
        callbackFun: timer.discard,
      },
    })
  }

  if (timer.status === TIMER_STATUS.IDLE) {
    return savedNotice ? <SavedNotice role="status">하루 기록에 저장했어요.</SavedNotice> : null
  }

  const isPreparing = timer.status === TIMER_STATUS.PREPARING
  const isUnsaved = [TIMER_STATUS.UNSAVED, TIMER_STATUS.SAVING].includes(timer.status)
  const isSaveUnknown = timer.status === TIMER_STATUS.SAVE_UNKNOWN
  const isStudy = timer.dailyLogType === 'STUDY'

  return (
    <TimerDock aria-label="현재 실행 중인 스톱워치">
      <TimerSummary>
        <SummaryToggle
          type="button"
          aria-expanded={expanded}
          aria-controls="global-timer-detail"
          onClick={() => setExpanded((value) => !value)}
        >
          <AccessTimeFilledRoundedIcon aria-hidden="true" />
          <SummaryText>
            <span><StatusDot $paused={[TIMER_STATUS.PAUSED, TIMER_STATUS.SAVE_UNKNOWN].includes(timer.status)} />{timer.isRemote ? '다른 탭에서 진행 중' : STATUS_LABEL[timer.status]} · {activityLabel}</span>
            <strong>{timer.contents || '활동 내용을 입력해주세요'}</strong>
          </SummaryText>
          <Elapsed aria-label={`경과 시간 ${formatTimerSeconds(timer.activityTimeSec)}`}>
            {formatTimerSeconds(timer.activityTimeSec)}
          </Elapsed>
          {expanded ? <ExpandMoreRoundedIcon aria-hidden="true" /> : <ExpandLessRoundedIcon aria-hidden="true" />}
        </SummaryToggle>

        {!timer.isRemote && !isPreparing && !isUnsaved && !isSaveUnknown && (
          <SummaryActions>
            {isStudy && (
              timer.status === TIMER_STATUS.PAUSED
                ? <Button onClick={timer.resume}><PlayArrowRoundedIcon aria-hidden="true" />계속</Button>
                : <Button $variant="secondary" onClick={timer.pause}><PauseRoundedIcon aria-hidden="true" />잠시 멈춤</Button>
            )}
            <Button $variant="danger" onClick={timer.stop}><StopRoundedIcon aria-hidden="true" />종료</Button>
          </SummaryActions>
        )}
      </TimerSummary>

      {expanded && (
        <TimerDetail id="global-timer-detail">
          <DetailFields>
            <label>
              <span>활동 유형</span>
              <SelectInput
                aria-label="활동 유형"
                value={timer.dailyLogType}
                disabled={!isPreparing || timer.isRemote}
                onChange={(event) => timer.updateDraft({ dailyLogType: event.target.value })}
              >
                {ACTIVITY_TYPES.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
              </SelectInput>
            </label>
            <label>
              <span>활동 내용</span>
              <TextInput
                value={timer.contents}
                disabled={timer.isRemote || (!isPreparing && !isUnsaved)}
                placeholder="예: 영어 단어 복습"
                aria-invalid={Boolean(validationMessage)}
                aria-describedby={validationMessage ? 'timer-validation-message' : undefined}
                onChange={(event) => {
                  setValidationMessage('')
                  timer.updateDraft({ contents: event.target.value })
                }}
              />
            </label>
          </DetailFields>

          {!isPreparing && (
            <TimeDetails>
              <span>전체 {formatTimerSeconds(timer.activityTimeSec)}</span>
              {isStudy && <span>집중 {formatTimerSeconds(timer.focusTimeSec)}</span>}
              {timer.startedAt && <span>{moment(timer.startedAt).format('HH:mm:ss')} 시작</span>}
            </TimeDetails>
          )}

          {validationMessage && <ValidationMessage id="timer-validation-message">{validationMessage}</ValidationMessage>}

          {!timer.isRemote && isSaveUnknown && (
            <RecoveryNotice role="status">
              저장 요청 중 화면이 닫혔어요. 하루 기록에서 저장 여부를 확인해 주세요.
            </RecoveryNotice>
          )}

          {timer.isRemote && (
            <RecoveryNotice role="status">
              다른 탭에서 이 스톱워치를 사용하고 있어요. 여기서 이어서 진행하면 다른 탭은 읽기 전용으로 바뀌어요.
            </RecoveryNotice>
          )}

          <DetailActions>
            {!timer.isRemote && isPreparing && (
              <>
                <Button $variant="quiet" onClick={timer.discard}>취소</Button>
                <Button onClick={handleStart}><PlayArrowRoundedIcon aria-hidden="true" />측정 시작</Button>
              </>
            )}
            {!timer.isRemote && isUnsaved && (
              <>
                <Button $variant="quiet" disabled={timer.status === TIMER_STATUS.SAVING} onClick={handleDiscard}>기록 삭제</Button>
                <Button disabled={timer.status === TIMER_STATUS.SAVING} onClick={handleSave}>
                  {timer.status === TIMER_STATUS.SAVING ? '저장 중…' : '하루 기록에 저장'}
                </Button>
              </>
            )}
            {!timer.isRemote && isSaveUnknown && (
              <>
                <Button $variant="secondary" onClick={() => navigate(ROUTES.DAILY_LOGS)}>하루 기록 확인</Button>
                <Button onClick={timer.confirmSaveMissing}>저장되지 않았어요</Button>
              </>
            )}
            {timer.isRemote && <Button onClick={timer.takeOwnership}>이 탭에서 이어서</Button>}
          </DetailActions>
        </TimerDetail>
      )}
    </TimerDock>
  )
}

const TimerDock = styled.section`
  position: fixed;
  z-index: 60;
  right: 16px;
  bottom: 16px;
  left: 240px;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.large};
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadow.medium};

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    right: 8px;
    bottom: calc(74px + env(safe-area-inset-bottom));
    left: 8px;
    max-height: calc(100dvh - 96px - env(safe-area-inset-bottom));
    overflow-y: auto;
  }
`

const TimerSummary = styled.div`
  display: flex;
  min-height: 68px;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    align-items: stretch;
    flex-direction: column;
  }
`

const SummaryToggle = styled.button`
  display: grid;
  min-width: 0;
  flex: 1;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 11px;
  padding: 6px;
  color: inherit;
  text-align: left;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.small};
  background: transparent;

  > svg:first-child { color: ${({ theme }) => theme.colors.muiPrimary}; }
  &:hover { background: ${({ theme }) => theme.colors.primarySoft}; }
`

const SummaryText = styled.span`
  min-width: 0;
  span, strong { display: block; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
  span { color: ${({ theme }) => theme.colors.textMuted}; font-size: 0.74rem; }
  strong { margin-top: 2px; font-size: 0.9rem; }
`

const StatusDot = styled.i`
  display: inline-block;
  width: 7px;
  height: 7px;
  margin-right: 6px;
  border-radius: 50%;
  background: ${({ $paused, theme }) => ($paused ? theme.colors.warning : theme.colors.success)};
`

const Elapsed = styled.time`
  color: ${({ theme }) => theme.colors.primary};
  font-variant-numeric: tabular-nums;
  font-size: 1.05rem;
  font-weight: 780;
`

const SummaryActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) { > button { flex: 1; } }
`

const TimerDetail = styled.div`
  padding: 16px 18px 18px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
`

const DetailFields = styled.div`
  display: grid;
  grid-template-columns: minmax(140px, 0.35fr) minmax(220px, 1fr);
  gap: 12px;
  label { display: grid; gap: 6px; color: ${({ theme }) => theme.colors.textMuted}; font-size: 0.78rem; font-weight: 650; }
  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) { grid-template-columns: 1fr; }
`

const TimeDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-top: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.78rem;
`

const RecoveryNotice = styled.p`
  margin: 12px 0 0;
  color: ${({ theme }) => theme.colors.warning};
  font-size: 0.8rem;
`

const ValidationMessage = styled.p`
  margin: 10px 0 0;
  color: ${({ theme }) => theme.colors.danger};
  font-size: 0.8rem;
`

const DetailActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
`

const SavedNotice = styled.div`
  position: fixed;
  z-index: 90;
  right: 20px;
  bottom: 20px;
  padding: 12px 16px;
  color: #FFFFFF;
  border-radius: ${({ theme }) => theme.radius.small};
  background: ${({ theme }) => theme.colors.success};
  box-shadow: ${({ theme }) => theme.shadow.medium};
  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) { bottom: calc(78px + env(safe-area-inset-bottom)); }
`
